import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Building2, BarChart3, Calendar, Award,
  Target, DollarSign, Shield, FileText, TrendingUp,
  CheckCircle2, ChevronRight, MapPin, Users, Home,
  Wrench, ClipboardCheck, PieChart, Lock, Briefcase
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const TrackRecordPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Performance overview cards
  const overviewMetrics = [
    { 
      icon: Calendar, 
      label: '18+ Years Active', 
      description: 'Continuous operations in Texas real estate since 2005' 
    },
    { 
      icon: Building2, 
      label: '70+ Deals Completed', 
      description: 'Acquisitions, repositioning, and successful exits' 
    },
    { 
      icon: Briefcase, 
      label: '3 Asset Classes', 
      description: 'Multifamily, commercial office, and retail properties' 
    },
    { 
      icon: MapPin, 
      label: 'Greater Houston Focus', 
      description: 'Deep market expertise across Texas submarkets' 
    }
  ];

  // Realized investments data
  const realizedInvestments = [
    {
      property: 'Hollyvale Apartments',
      market: 'Houston, TX',
      assetType: 'Multifamily + Storage',
      holdPeriod: '5 Years',
      acquired: '2013',
      exited: '2018',
      irr: '28%',
      multiple: '2.4x'
    },
    {
      property: 'Westwood Office Complex',
      market: 'Houston, TX',
      assetType: 'Office',
      holdPeriod: '5 Years',
      acquired: '2015',
      exited: '2020',
      irr: '24%',
      multiple: '2.1x'
    },
    {
      property: 'Cypress Creek Retail',
      market: 'Houston, TX',
      assetType: 'Retail',
      holdPeriod: '5 Years',
      acquired: '2016',
      exited: '2021',
      irr: '22%',
      multiple: '1.9x'
    }
  ];

  // Active portfolio indicators
  const activeIndicators = [
    { 
      icon: Users, 
      title: 'Occupancy Trends', 
      description: '94% average occupancy across active holdings with positive absorption trends' 
    },
    { 
      icon: TrendingUp, 
      title: 'NOI Growth', 
      description: 'Year-over-year net operating income improvements through operational execution' 
    },
    { 
      icon: Wrench, 
      title: 'Renovation Progress', 
      description: '75% of planned unit upgrades completed across multifamily assets' 
    },
    { 
      icon: BarChart3, 
      title: 'Operational Efficiency', 
      description: 'Expense ratio improvements through vendor consolidation and management' 
    }
  ];

  // Performance metrics definitions
  const metricDefinitions = [
    {
      term: 'IRR',
      fullName: 'Internal Rate of Return',
      definition: 'The annualized return on investment that accounts for the timing and magnitude of all cash flows, including capital contributions and distributions.',
      whyUsed: 'Investors use IRR to compare performance across investments with different hold periods and cash flow profiles.'
    },
    {
      term: 'Equity Multiple',
      fullName: 'Total Return on Invested Capital',
      definition: 'The total amount of distributions returned to investors divided by the total equity invested, expressed as a multiple (e.g., 2.0x means investors received twice their initial investment).',
      whyUsed: 'Equity multiples show total return regardless of time, helping investors understand absolute profit potential.'
    },
    {
      term: 'Cash-on-Cash',
      fullName: 'Annual Cash Yield',
      definition: 'The annual cash distributions as a percentage of the original equity invested, measuring ongoing income generation before property sale.',
      whyUsed: 'This metric helps income-focused investors evaluate current yield and compare to other income-producing assets.'
    }
  ];

  // Value creation steps
  const valueCreationSteps = [
    { 
      step: 1, 
      title: 'Acquisition & Underwriting', 
      description: 'Disciplined sourcing and conservative underwriting to identify properties with value-add potential at appropriate entry points.' 
    },
    { 
      step: 2, 
      title: 'Renovation Execution', 
      description: 'Strategic unit upgrades and common area improvements to capture rent premiums and attract quality tenants.' 
    },
    { 
      step: 3, 
      title: 'Leasing Optimization', 
      description: 'Professional leasing strategy focused on tenant quality, retention programs, and occupancy stabilization.' 
    },
    { 
      step: 4, 
      title: 'Expense Management', 
      description: 'Operational efficiencies through vendor consolidation, utility optimization, and institutional management practices.' 
    },
    { 
      step: 5, 
      title: 'Stabilization & Exit', 
      description: 'Strategic disposition timing to maximize exit value and return capital to investors.' 
    }
  ];

  // Investor reporting features
  const reportingFeatures = [
    { icon: FileText, text: 'Quarterly performance reports with property-level detail' },
    { icon: Lock, text: 'Secure investor portal with 24/7 document access' },
    { icon: PieChart, text: 'Annual K-1 tax documents delivered on schedule' },
    { icon: Building2, text: 'Ongoing asset updates and market commentary' }
  ];

  return (
    <>
      <Helmet>
        <title>Real Estate Investment Track Record | ASAP Capital</title>
        <meta 
          name="description" 
          content="Review ASAP Capital's real estate investment track record, including realized deals, performance metrics, and value-add execution across Texas." 
        />
        <meta name="keywords" content="real estate investment track record, private equity performance, multifamily investment results, Houston real estate returns" />
        <link rel="canonical" href="/track-record" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO + INTRO ==================== */}
          <section className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
            {/* Subtle background */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/3 rounded-full blur-[150px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-2xl"
              >
                {/* H1 */}
                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.25] mb-6"
                  variants={itemVariants}
                >
                  Our Investment Track Record
                </motion.h1>

                {/* Intro paragraph */}
                <motion.p 
                  className="text-base lg:text-lg text-white/60 leading-relaxed mb-6"
                  variants={itemVariants}
                >
                  Review our historical performance across completed and active real estate investments 
                  in the Greater Houston market. Our track record reflects disciplined execution of 
                  value-add strategies across multifamily and commercial asset classes.
                </motion.p>

                {/* Compliance note */}
                <motion.p 
                  className="text-sm text-white/35 leading-relaxed"
                  variants={itemVariants}
                >
                  Past performance is not indicative of future results. All investments involve risk. 
                  Please refer to the Private Placement Memorandum for complete disclosures.
                </motion.p>
              </motion.div>
            </div>
          </section>

          {/* ==================== PERFORMANCE OVERVIEW ==================== */}
          <section className="py-16 lg:py-24 relative bg-[#0d1a2d]/50">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
              >
                <motion.h2 
                  className="text-xl lg:text-2xl font-bold text-white mb-10"
                  variants={itemVariants}
                >
                  Performance Overview
                </motion.h2>

                {/* Metric cards grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {overviewMetrics.map((metric, index) => (
                    <motion.div
                      key={metric.label}
                      className="bg-[#111D35]/60 rounded-xl p-5 border border-white/8"
                      variants={itemVariants}
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                        <metric.icon className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-white font-semibold mb-2">{metric.label}</h3>
                      <p className="text-white/45 text-sm leading-relaxed">{metric.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== REALIZED INVESTMENTS ==================== */}
          <section className="py-16 lg:py-24 relative bg-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
              >
                <motion.h2 
                  className="text-xl lg:text-2xl font-bold text-white mb-10"
                  variants={itemVariants}
                >
                  Realized Investments
                </motion.h2>

                {/* Desktop Table */}
                <motion.div 
                  className="hidden lg:block overflow-hidden rounded-xl border border-white/10"
                  variants={itemVariants}
                >
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#111D35]/80 border-b border-white/10">
                        <th className="text-left text-white/50 text-sm font-medium py-4 px-5">Property</th>
                        <th className="text-left text-white/50 text-sm font-medium py-4 px-5">Market</th>
                        <th className="text-left text-white/50 text-sm font-medium py-4 px-5">Asset Type</th>
                        <th className="text-left text-white/50 text-sm font-medium py-4 px-5">Hold Period</th>
                        <th className="text-center text-white/50 text-sm font-medium py-4 px-5">IRR</th>
                        <th className="text-center text-white/50 text-sm font-medium py-4 px-5">Multiple</th>
                      </tr>
                    </thead>
                    <tbody>
                      {realizedInvestments.map((investment, index) => (
                        <tr 
                          key={investment.property}
                          className={`border-b border-white/5 ${index % 2 === 0 ? 'bg-[#0A1628]' : 'bg-[#111D35]/30'}`}
                        >
                          <td className="py-4 px-5">
                            <span className="text-white font-medium">{investment.property}</span>
                          </td>
                          <td className="py-4 px-5 text-white/60">{investment.market}</td>
                          <td className="py-4 px-5 text-white/60">{investment.assetType}</td>
                          <td className="py-4 px-5 text-white/60">{investment.holdPeriod}</td>
                          <td className="py-4 px-5 text-center">
                            <span className="text-[#D4AF37] font-semibold">{investment.irr}</span>
                          </td>
                          <td className="py-4 px-5 text-center">
                            <span className="text-white font-semibold">{investment.multiple}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </motion.div>

                {/* Mobile Cards */}
                <motion.div 
                  className="lg:hidden space-y-4"
                  variants={itemVariants}
                >
                  {realizedInvestments.map((investment) => (
                    <div 
                      key={investment.property}
                      className="bg-[#111D35]/50 rounded-xl p-5 border border-white/10"
                    >
                      <h3 className="text-white font-semibold mb-3">{investment.property}</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                        <div>
                          <span className="text-white/40 block">Market</span>
                          <span className="text-white/70">{investment.market}</span>
                        </div>
                        <div>
                          <span className="text-white/40 block">Asset Type</span>
                          <span className="text-white/70">{investment.assetType}</span>
                        </div>
                        <div>
                          <span className="text-white/40 block">Hold Period</span>
                          <span className="text-white/70">{investment.holdPeriod}</span>
                        </div>
                      </div>
                      <div className="flex gap-4 pt-3 border-t border-white/10">
                        <div className="flex-1 text-center">
                          <span className="text-white/40 text-xs block mb-1">IRR</span>
                          <span className="text-[#D4AF37] font-bold text-lg">{investment.irr}</span>
                        </div>
                        <div className="flex-1 text-center">
                          <span className="text-white/40 text-xs block mb-1">Multiple</span>
                          <span className="text-white font-bold text-lg">{investment.multiple}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>

                {/* Compliance disclaimer */}
                <motion.div 
                  className="mt-6 p-4 bg-[#111D35]/30 rounded-lg border border-white/5"
                  variants={itemVariants}
                >
                  <p className="text-white/30 text-xs leading-relaxed">
                    * Historical performance shown above represents actual returns from completed investments. 
                    Past performance is not indicative of future results. Returns are not guaranteed and actual 
                    results may vary significantly. Please review the PPM for complete risk disclosures.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ACTIVE PORTFOLIO INDICATORS ==================== */}
          <section className="py-16 lg:py-24 relative bg-[#0d1a2d]/50">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
              >
                <motion.h2 
                  className="text-xl lg:text-2xl font-bold text-white mb-10"
                  variants={itemVariants}
                >
                  Active Portfolio Indicators
                </motion.h2>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {activeIndicators.map((indicator, index) => (
                    <motion.div
                      key={indicator.title}
                      className="bg-[#111D35]/60 rounded-xl p-5 border border-white/8"
                      variants={itemVariants}
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                        <indicator.icon className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-white font-semibold mb-2">{indicator.title}</h3>
                      <p className="text-white/45 text-sm leading-relaxed">{indicator.description}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PERFORMANCE METRICS EXPLAINED ==================== */}
          <section className="py-16 lg:py-24 relative bg-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
              >
                <motion.h2 
                  className="text-xl lg:text-2xl font-bold text-white mb-10"
                  variants={itemVariants}
                >
                  Performance Metrics Explained
                </motion.h2>

                <div className="grid md:grid-cols-3 gap-6">
                  {metricDefinitions.map((metric, index) => (
                    <motion.div
                      key={metric.term}
                      className="bg-[#111D35]/50 rounded-xl p-6 border border-white/10"
                      variants={itemVariants}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-[#D4AF37] font-bold text-xl">{metric.term}</span>
                        <span className="text-white/40 text-sm">|</span>
                        <span className="text-white/50 text-sm">{metric.fullName}</span>
                      </div>
                      <p className="text-white/60 text-sm leading-relaxed mb-4">
                        {metric.definition}
                      </p>
                      <p className="text-white/40 text-sm leading-relaxed pt-4 border-t border-white/10">
                        <span className="text-white/50 font-medium">Why it matters: </span>
                        {metric.whyUsed}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== VALUE CREATION STRATEGY ==================== */}
          <section className="py-16 lg:py-24 relative bg-[#0d1a2d]/50">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
              >
                <motion.h2 
                  className="text-xl lg:text-2xl font-bold text-white mb-10"
                  variants={itemVariants}
                >
                  Value Creation Strategy
                </motion.h2>

                {/* Step flow */}
                <div className="relative">
                  {/* Connector line - desktop */}
                  <div className="hidden lg:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-[#D4AF37]/50 via-[#D4AF37]/30 to-[#D4AF37]/10" />
                  
                  <div className="grid lg:grid-cols-5 gap-6">
                    {valueCreationSteps.map((step, index) => (
                      <motion.div
                        key={step.step}
                        className="relative"
                        variants={itemVariants}
                      >
                        {/* Step number */}
                        <div className="w-16 h-16 rounded-full bg-[#111D35] border-2 border-[#D4AF37]/50 flex items-center justify-center mb-4 relative z-10">
                          <span className="text-[#D4AF37] font-bold text-lg">{step.step}</span>
                        </div>
                        
                        <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                        <p className="text-white/45 text-sm leading-relaxed">{step.description}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== INVESTMENT CASE STUDIES ==================== */}
          <section className="py-16 lg:py-24 relative bg-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
              >
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Left - Text */}
                  <div>
                    <motion.h2 
                      className="text-xl lg:text-2xl font-bold text-white mb-5"
                      variants={itemVariants}
                    >
                      Investment Case Studies
                    </motion.h2>
                    <motion.p 
                      className="text-white/55 leading-relaxed"
                      variants={itemVariants}
                    >
                      Explore detailed analyses of our investment approach, including acquisition 
                      criteria, value-add execution, and operational improvements for specific 
                      properties in our portfolio.
                    </motion.p>
                  </div>

                  {/* Right - CTA Card */}
                  <motion.div variants={itemVariants}>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-8 border border-white/10">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-[#D4AF37]" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">View Portfolio</h3>
                          <p className="text-white/40 text-sm">Explore our current holdings</p>
                        </div>
                      </div>
                      <Link to="/portfolio">
                        <Button className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold py-3 h-12 rounded-lg text-sm flex items-center justify-center gap-2">
                          View Case Studies
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== INVESTOR REPORTING ==================== */}
          <section className="py-16 lg:py-24 relative bg-[#0d1a2d]/50">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
              >
                <motion.h2 
                  className="text-xl lg:text-2xl font-bold text-white mb-10"
                  variants={itemVariants}
                >
                  Investor Reporting
                </motion.h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  {reportingFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-4 p-4 bg-[#111D35]/40 rounded-lg border border-white/5"
                      variants={itemVariants}
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <span className="text-white/60 text-sm leading-relaxed pt-2">{feature.text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== RISK & DISCLOSURES ==================== */}
          <section className="py-16 lg:py-20 relative bg-[#0A1628]">
            <div className="max-w-[900px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
              >
                <motion.div 
                  className="bg-amber-500/5 rounded-xl p-6 lg:p-8 border border-amber-500/20"
                  variants={itemVariants}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="h-5 w-5 text-amber-400" />
                    <h2 className="text-lg font-semibold text-white">Risk & Disclosures</h2>
                  </div>
                  <div className="space-y-3 text-white/50 text-sm leading-relaxed">
                    <p>
                      <strong className="text-white/60">No Guarantee of Future Results:</strong> Past performance 
                      is historical and does not guarantee or predict future performance. The returns shown 
                      represent completed investments and may not be repeated.
                    </p>
                    <p>
                      <strong className="text-white/60">Investment Risk:</strong> All investments involve risk, 
                      including the potential loss of principal. Real estate investments are illiquid and 
                      subject to market conditions, operational risks, and economic factors.
                    </p>
                    <p>
                      <strong className="text-white/60">Governing Documents:</strong> The Private Placement 
                      Memorandum (PPM) governs all investment terms, including complete risk disclosures, 
                      fees, and investment requirements.
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EXPLORE OPPORTUNITIES CTA ==================== */}
          <section className="py-16 lg:py-24 relative bg-[#111D35]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={containerVariants}
                className="text-center"
              >
                <motion.h2 
                  className="text-2xl lg:text-3xl font-bold text-white mb-5"
                  variants={itemVariants}
                >
                  Explore Opportunities
                </motion.h2>
                <motion.p 
                  className="text-white/50 max-w-xl mx-auto mb-8"
                  variants={itemVariants}
                >
                  Ready to learn more about investing with ASAP Capital? 
                  Schedule a consultation or review our fund terms.
                </motion.p>
                
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-8 py-3 h-12 rounded-full text-sm">
                      Schedule Consultation
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                  <Link to="/fund-terms">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-12 rounded-full text-sm">
                      Review Fund Terms
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TrackRecordPage;
